/*
 * StreamBank Admin v38.0
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React, {useCallback, useEffect, useRef} from 'react';
import PropTypes from 'prop-types'

import {Conditional} from "../Conditional";

import * as renderers from './ModelRenderers';
import {
    useFieldDefinitionVisibility,
} from "../../Hooks/GenericModelHooks";
import {useGenericModelValidations} from "../../Hooks/GenericModelValidationHooks";

const GenericModelViewerComponent = (props) => {

    const {
        Association: association,
        CustomData: customData,
        Data: data = {},
        DefaultSelectedFieldDefKey,
        EditMode: editMode,
        HideGroups: hiddenGroupKeys = [],
        HideFields: hiddenFieldKeys = [],
        IsNewModel,
        Model: model,
        RootData: rootData,
        SkipBorder,
        Update: onUpdate,
    } = props;
    
    const fieldDefinitionVisibility = useFieldDefinitionVisibility(props);
    const fieldDefinitionGroups = model?.getFieldDefGroups?.() ?? [];
    
    // hides groups with hiddenGroupKeys
    // and fields based on permissions, etc
    const visibleFieldDefinitionGroups = fieldDefinitionVisibility(fieldDefinitionGroups, hiddenGroupKeys, hiddenFieldKeys);
    
    const {validations, childValidations, onChildValidation, editorGuid} = useGenericModelValidations(props, visibleFieldDefinitionGroups);

    useEffect(() => {
        //set default values for FieldDefs.
        let updates = [];

        for(const fieldDefinitionGroup of fieldDefinitionGroups) {
            for(const fieldDef of fieldDefinitionGroup.value) {
                if (fieldDef.defaultValue) { // if there is a default value
                    let value = fieldDef.getter(data); //get the current

                    if (value == null) { // if it's blank
                        updates.push({ // set to the default.
                            def: fieldDef,
                            value: fieldDef.defaultValue
                        });
                    }
                }
            }
        }
        
        updateFields(updates);
        
    }, []);
    
    // Refs keep the latest data and onUpdate without needing them as deps,
    // so updateFields is stable for the lifetime of the component.
    // (React 17 legacy renderer — synchronous commits make this safe.)
    const dataRef = useRef(data);
    dataRef.current = data;
    const onUpdateRef = useRef(onUpdate);
    onUpdateRef.current = onUpdate;

    const updateFields = useCallback((values) => {
        if (!values.length) return;
        let target = { ...dataRef.current };
        for (const { def, value } of values) {
            // value may itself be a functional updater passed up from a nested
            // ChildrenFieldEditor — resolve it against the current field value
            const resolvedValue = typeof value === 'function'
                ? value(def.getter(dataRef.current))
                : value;
            def?.setter?.(target, resolvedValue);
        }
        onUpdateRef.current?.(target);
    }, []) // stable — reads latest data/onUpdate via refs at call time

    const updateField = useCallback((def, value) => {
        updateFields([{ def, value }]);
    }, [updateFields])
    
    const getOptionsForGroup = (fieldGroup) => {
        return {
            renderer: model.groupRenderers?.[fieldGroup.key] ?? 'StandardModelViewerRenderer'
        }
    }
    
    const getFieldDefs = useCallback((includeHidden) => includeHidden ? fieldDefinitionGroups : visibleFieldDefinitionGroups, [visibleFieldDefinitionGroups])

    const renderFieldGroup = (fieldGroup) => {
        let groupOptions = getOptionsForGroup(fieldGroup);
        const RendererComponent = renderers?.[groupOptions.renderer];

        return   <Conditional key={fieldGroup.key} Condition={(fieldGroup.value ?? []).length > 0}>
                    <Conditional Condition={fieldGroup.key && fieldGroup.groupTitle !== '' && fieldGroup.groupTitle != null} key="header">
                        <h3 className="table-headers">{fieldGroup.groupTitle}</h3>
                    </Conditional>
                    <RendererComponent
                        key="body"
                        FieldGroup={fieldGroup}
                        SkipBorder={SkipBorder}
                        EditMode={editMode}
                        RootData={rootData || data}
                        Data={data}
                        Validation={validations}
                        UpdateFields={updateFields}
                        UpdateField={updateField}
                        FieldDefinitionGroups={fieldDefinitionGroups}
                        GetFieldDefs={getFieldDefs}
                        
                        // called when a CHILD GenericModelViewer updates
                        // its own internal validation state
                        OnValidationUpdate={onChildValidation}
                        CustomData={customData}
                        IsNewModel={IsNewModel}
                        DefaultSelectedFieldDefKey={DefaultSelectedFieldDefKey}
                    />
                </Conditional>;
    }

    let classNames = ["sb-generic-model-viewer"]

    if(typeof model?.classNamesFromData == "function"){
        classNames = classNames.concat(model?.classNamesFromData(data) ?? []);
    }

    return   <div className={classNames.join(' ')} onSubmit={e => e.preventDefault()}>
                    <Conditional Condition={validations[null]}>
                        <div className="sb-model-viewer-validation-failure-text">
                            {validations[null]?.message}
                        </div>
                    </Conditional>
                    {
                        visibleFieldDefinitionGroups.map((fieldGroup) =>
                            renderFieldGroup(fieldGroup)
                        )
                    }
            </div>;

}


GenericModelViewerComponent.propTypes = {
  Association: PropTypes.object,
  CustomData: PropTypes.object,
  Data: PropTypes.object,
  DefaultSelectedFieldDefKey: PropTypes.string,
  EditMode: PropTypes.bool,
  HasPermission: PropTypes.any,
  HideGroups: PropTypes.any,
  IsNewModel: PropTypes.bool,
  Model: PropTypes.any.isRequired,
  OnValidationUpdate: PropTypes.func,
  RootData: PropTypes.object,
  SkipBorder: PropTypes.bool,
  Update: PropTypes.func
}

GenericModelViewerComponent.defaultProps = {
  EditMode: false,
  IsNewModel: false,
  SkipBorder: false
}

// RootData is the root survey object — it changes reference on every keystroke
// because the survey is updated immutably.  But child GMVs only use RootData for
// cross-field visibility queries that reference the item's own fields (e.g.
// surveyQuestionDataTypeId), so a new RootData reference doesn't change anything
// they render.  Excluding it prevents needless re-renders of every section/question
// GMV whenever any field anywhere in the survey changes.
// Note RootData also never reaches list-item GMVs at all (MutableList doesn't
// forward it), so a visibility query needing context from outside the item —
// e.g. an ancestor record's field — must receive it via CustomData, which is
// forwarded through the list chain AND compared here.
function gmvPropsAreEqual(prev, next) {
    return prev.Data === next.Data
        && prev.EditMode === next.EditMode
        && prev.IsNewModel === next.IsNewModel
        && prev.Model === next.Model
        && prev.Association === next.Association
        && prev.SkipBorder === next.SkipBorder
        && prev.CustomData === next.CustomData;
    // RootData: intentionally excluded (see above)
    // Update, OnValidationUpdate: stable callbacks — changes would be bugs, not features
    // HideGroups, HideFields, DefaultSelectedFieldDefKey: stable in survey builder context
}
export const GenericModelViewer = React.memo(GenericModelViewerComponent, gmvPropsAreEqual);
